import React, { useState } from 'react';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Calendar } from './ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, Repeat } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type RepeatFrequency = 'never' | 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface RepeatSchedule {
  frequency: RepeatFrequency;
  interval: number;
  onDay?: number; // 0-6 for Sunday-Saturday (weekly)
  onDate?: number; // 1-31 for day of month (monthly/yearly)
  onMonth?: number; // 1-12 for month (yearly only)
  endType: 'never' | 'on' | 'after';
  endDate?: string;
  endCount?: number;
}

interface RepeatScheduleSelectorProps {
  value: RepeatSchedule;
  onChange: (schedule: RepeatSchedule) => void;
  startDate: Date;
}

const DAYS_OF_WEEK_EN = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const DAYS_OF_WEEK_HE = [
  'ראשון',
  'שני',
  'שלישי',
  'רביעי',
  'חמישי',
  'שישי',
  'שבת',
];

const MONTHS_EN = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const MONTHS_HE = [
  'ינואר',
  'פברואר',
  'מרץ',
  'אפריל',
  'מאי',
  'יוני',
  'יולי',
  'אוגוסט',
  'ספטמבר',
  'אוקטובר',
  'נובמבר',
  'דצמבר',
];

export function RepeatScheduleSelector({
  value,
  onChange,
  startDate,
}: RepeatScheduleSelectorProps) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const [isOpen, setIsOpen] = useState(false);

  const DAYS_OF_WEEK = isRTL ? DAYS_OF_WEEK_HE : DAYS_OF_WEEK_EN;
  const MONTHS = isRTL ? MONTHS_HE : MONTHS_EN;

  const handleFrequencyChange = (frequency: RepeatFrequency) => {
    const newSchedule: RepeatSchedule = {
      ...value,
      frequency,
      interval: 1,
    };

    // Set defaults based on frequency
    if (frequency === 'weekly') {
      newSchedule.onDay = startDate.getDay();
    } else if (frequency === 'monthly') {
      newSchedule.onDate = startDate.getDate();
    } else if (frequency === 'yearly') {
      newSchedule.onDate = startDate.getDate();
      newSchedule.onMonth = startDate.getMonth() + 1;
    }

    onChange(newSchedule);
  };

  const getRepeatText = () => {
    if (value.frequency === 'never') {
      return t('periodicPayments.repeat.never', 'Never');
    }

    let text = t('periodicPayments.repeat.every', 'Every') + ' ';

    if (value.interval > 1) {
      text += `${value.interval} `;
    }

    switch (value.frequency) {
      case 'daily':
        text += value.interval === 1 
          ? t('periodicPayments.repeat.day', 'day') 
          : t('periodicPayments.repeat.days', 'days');
        break;
      case 'weekly':
        text += value.interval === 1 
          ? t('periodicPayments.repeat.week', 'week') 
          : t('periodicPayments.repeat.weeks', 'weeks');
        if (value.onDay !== undefined) {
          text += ` ${t('periodicPayments.repeat.on', 'on')} ${DAYS_OF_WEEK[value.onDay]}`;
        }
        break;
      case 'monthly':
        text += value.interval === 1 
          ? t('periodicPayments.repeat.month', 'month') 
          : t('periodicPayments.repeat.months', 'months');
        if (value.onDate) {
          text += ` ${t('periodicPayments.repeat.onDay', 'on day')} ${value.onDate}`;
        }
        break;
      case 'yearly':
        text += value.interval === 1 
          ? t('periodicPayments.repeat.year', 'year') 
          : t('periodicPayments.repeat.years', 'years');
        if (value.onMonth && value.onDate) {
          text += ` ${t('periodicPayments.repeat.on', 'on')} ${MONTHS[value.onMonth - 1]} ${value.onDate}`;
        }
        break;
    }

    // Add end condition
    if (value.endType === 'on' && value.endDate) {
      text += `, ${t('periodicPayments.repeat.until', 'until')} ${format(new Date(value.endDate), 'dd/MM/yyyy')}`;
    } else if (value.endType === 'after' && value.endCount) {
      text += `, ${value.endCount} ${t('periodicPayments.repeat.times', 'times')}`;
    }

    return text;
  };

  const getFrequencyUnitText = () => {
    switch (value.frequency) {
      case 'daily':
        return value.interval === 1 
          ? t('periodicPayments.repeat.day', 'day') 
          : t('periodicPayments.repeat.days', 'days');
      case 'weekly':
        return value.interval === 1 
          ? t('periodicPayments.repeat.week', 'week') 
          : t('periodicPayments.repeat.weeks', 'weeks');
      case 'monthly':
        return value.interval === 1 
          ? t('periodicPayments.repeat.month', 'month') 
          : t('periodicPayments.repeat.months', 'months');
      case 'yearly':
        return value.interval === 1 
          ? t('periodicPayments.repeat.year', 'year') 
          : t('periodicPayments.repeat.years', 'years');
      default:
        return '';
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-between"
          type="button"
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          <span className="flex items-center gap-2">
            <Repeat className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className={isRTL ? 'text-right' : 'text-left'}>{getRepeatText()}</span>
          </span>
          <CalendarIcon className="h-4 w-4 text-muted-foreground shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 sm:w-96 p-4" 
        align="start"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="space-y-4">
          {/* Frequency Selection */}
          <div className="space-y-2">
            <Label className={`block ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('periodicPayments.repeat.frequency', 'Repeat')}
            </Label>
            <Select value={value.frequency} onValueChange={handleFrequencyChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="never">{t('periodicPayments.repeat.never', 'Never')}</SelectItem>
                <SelectItem value="daily">{t('periodicPayments.repeat.daily', 'Daily')}</SelectItem>
                <SelectItem value="weekly">{t('periodicPayments.repeat.weekly', 'Weekly')}</SelectItem>
                <SelectItem value="monthly">{t('periodicPayments.repeat.monthly', 'Monthly')}</SelectItem>
                <SelectItem value="yearly">{t('periodicPayments.repeat.yearly', 'Yearly')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {value.frequency !== 'never' && (
            <>
              {/* Interval Selection */}
              <div className="space-y-2">
                <Label className={`block ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('periodicPayments.repeat.every', 'Every')}
                </Label>
                <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Input
                    type="number"
                    min="1"
                    max="52"
                    value={value.interval}
                    onChange={(e) =>
                      onChange({ ...value, interval: Math.max(1, parseInt(e.target.value) || 1) })
                    }
                    className="w-20"
                  />
                  <span className="text-sm text-muted-foreground">
                    {getFrequencyUnitText()}
                  </span>
                </div>
              </div>

              {/* Day of Week Selection (Weekly) */}
              {value.frequency === 'weekly' && (
                <div className="space-y-2">
                  <Label className={`block ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('periodicPayments.repeat.repeatOn', 'Repeat on')}
                  </Label>
                  <Select
                    value={value.onDay?.toString()}
                    onValueChange={(v) =>
                      onChange({ ...value, onDay: parseInt(v) })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS_OF_WEEK.map((day, index) => (
                        <SelectItem key={index} value={index.toString()}>
                          {day}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Day of Month Selection (Monthly) */}
              {value.frequency === 'monthly' && (
                <div className="space-y-2">
                  <Label className={`block ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('periodicPayments.repeat.dayOfMonth', 'Day of month')}
                  </Label>
                  <Select
                    value={value.onDate?.toString()}
                    onValueChange={(v) =>
                      onChange({ ...value, onDate: parseInt(v) })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                        <SelectItem key={day} value={day.toString()}>
                          {day}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Month and Day Selection (Yearly) */}
              {value.frequency === 'yearly' && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className={`block ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('periodicPayments.repeat.monthLabel', 'Month')}
                    </Label>
                    <Select
                      value={value.onMonth?.toString()}
                      onValueChange={(v) =>
                        onChange({ ...value, onMonth: parseInt(v) })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.map((month, index) => (
                          <SelectItem key={index} value={(index + 1).toString()}>
                            {month}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className={`block ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('periodicPayments.repeat.dayLabel', 'Day')}
                    </Label>
                    <Select
                      value={value.onDate?.toString()}
                      onValueChange={(v) =>
                        onChange({ ...value, onDate: parseInt(v) })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                          <SelectItem key={day} value={day.toString()}>
                            {day}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* End Condition */}
              <div className="space-y-2">
                <Label className={`block ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('periodicPayments.repeat.endRepeat', 'End repeat')}
                </Label>
                <RadioGroup
                  value={value.endType}
                  onValueChange={(v: 'never' | 'on' | 'after') => onChange({ ...value, endType: v })}
                  className="space-y-2"
                >
                  <div className={`flex items-center space-x-2 ${isRTL ? 'flex-row-reverse space-x-reverse' : ''}`}>
                    <RadioGroupItem value="never" id="end-never" />
                    <Label htmlFor="end-never" className="font-normal cursor-pointer">
                      {t('periodicPayments.repeat.never', 'Never')}
                    </Label>
                  </div>

                  <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <RadioGroupItem value="on" id="end-on" />
                    <Label htmlFor="end-on" className="font-normal cursor-pointer">
                      {t('periodicPayments.repeat.onDateLabel', 'On date')}
                    </Label>
                    {value.endType === 'on' && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" type="button">
                            {value.endDate
                              ? format(new Date(value.endDate), 'dd/MM/yyyy')
                              : t('periodicPayments.repeat.pickDate', 'Pick date')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={
                              value.endDate ? new Date(value.endDate) : undefined
                            }
                            onSelect={(date) =>
                              onChange({
                                ...value,
                                endDate: date?.toISOString().split('T')[0],
                              })
                            }
                            disabled={(date) => date < startDate}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>

                  <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <RadioGroupItem value="after" id="end-after" />
                    <Label htmlFor="end-after" className="font-normal cursor-pointer">
                      {t('periodicPayments.repeat.after', 'After')}
                    </Label>
                    {value.endType === 'after' && (
                      <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Input
                          type="number"
                          min="1"
                          value={value.endCount || ''}
                          onChange={(e) =>
                            onChange({
                              ...value,
                              endCount: Math.max(1, parseInt(e.target.value) || 1),
                            })
                          }
                          className="w-20"
                        />
                        <span className="text-sm text-muted-foreground">
                          {t('periodicPayments.repeat.times', 'times')}
                        </span>
                      </div>
                    )}
                  </div>
                </RadioGroup>
              </div>
            </>
          )}

          <Button 
            onClick={() => setIsOpen(false)} 
            className="w-full"
            type="button"
          >
            {t('common.done', 'Done')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Helper function to convert old payment type to new frequency
export function mapOldPaymentTypeToFrequency(paymentType: string): RepeatFrequency {
  switch (paymentType) {
    case 'weekly':
      return 'weekly';
    case 'monthly':
    case 'periodic':
      return 'monthly';
    case 'yearly':
      return 'yearly';
    case 'installment':
      return 'monthly';
    default:
      return 'monthly';
  }
}

// Helper function to create RepeatSchedule from old payment data (backward compatibility)
export function createRepeatScheduleFromPayment(payment: any): RepeatSchedule {
  if (payment.repeatFrequency) {
    // New format - use as is
    return {
      frequency: payment.repeatFrequency,
      interval: payment.repeatInterval || 1,
      onDay: payment.repeatOnDay,
      onDate: payment.repeatOnDate,
      onMonth: payment.repeatOnMonth,
      endType: payment.repeatUntil ? 'on' : payment.repeatCount ? 'after' : 'never',
      endDate: payment.repeatUntil,
      endCount: payment.repeatCount,
    };
  }
  
  // Old format - convert to new format
  return {
    frequency: mapOldPaymentTypeToFrequency(payment.paymentType),
    interval: 1,
    onDate: payment.dayOfMonth,
    endType: payment.expirationDate ? 'on' : payment.totalPayments ? 'after' : 'never',
    endDate: payment.expirationDate,
    endCount: payment.totalPayments,
  };
}

// Helper function to convert RepeatSchedule to API fields
export function repeatScheduleToApiFields(schedule: RepeatSchedule) {
  return {
    repeatFrequency: schedule.frequency,
    repeatInterval: schedule.interval,
    repeatOnDay: schedule.onDay,
    repeatOnDate: schedule.onDate,
    repeatOnMonth: schedule.onMonth,
    repeatUntil: schedule.endType === 'on' ? schedule.endDate : undefined,
    repeatCount: schedule.endType === 'after' ? schedule.endCount : undefined,
    // Backward compatibility fields
    dayOfMonth: schedule.onDate || 1,
    expirationDate: schedule.endType === 'on' ? schedule.endDate : undefined,
    totalPayments: schedule.endType === 'after' ? schedule.endCount : undefined,
  };
}
